import type { FeeStructure, FeeStructureItem } from "../../services/financialService";
import type { ExportColumn } from "../../utils/exportTable";

export const FEE_STRUCTURE_ORDERINGS = {
  "-term": "Term (latest first)",
  term: "Term (earliest first)",
  level: "Class level",
  name: "Name (A–Z)",
  "-total": "Amount (highest first)",
} as const;

export const FEE_STRUCTURE_PAGE_SIZES = [10, 25, 50];

export interface FeeStructureFilters {
  search: string;
  academicYear: string;
  term: "" | "term_1" | "term_2" | "term_3";
  level: string;
  invoicing: "all" | "pending" | "done";
  ordering: keyof typeof FEE_STRUCTURE_ORDERINGS;
}

export const EMPTY_FEE_STRUCTURE_FILTERS: FeeStructureFilters = {
  search: "",
  academicYear: "",
  term: "",
  level: "",
  invoicing: "all",
  ordering: "-term",
};

/** What the filters need to know about the school calendar and class order. */
export interface FeeStructureContext {
  /** The AcademicTerm a structure bills for, looked up by id. */
  termOf: (termId: number) => { term: string; start_date?: string } | undefined;
  /** Class levels in school order (Baby, Middle, Top, P1 ...). */
  levels: string[];
}

export const countFeeStructureFilters = (filters: FeeStructureFilters) =>
  [filters.search.trim(), filters.academicYear, filters.term, filters.level].filter(Boolean).length;

const itemApplies = (item: FeeStructureItem, residence: "day" | "boarding", transport: boolean) =>
  (!item.applies_to || item.applies_to === "all" || item.applies_to === residence) &&
  (transport || !item.transport_only);

/**
 * What one learner is billed. Day and boarding learners pay different fees,
 * and transport-only fees reach only learners on school transport, so a
 * structure can bill up to four different amounts.
 */
export function structureBilling(structure: FeeStructure) {
  const billed = (residence: "day" | "boarding", transport: boolean) =>
    structure.items
      .filter((item) => itemApplies(item, residence, transport))
      .reduce((sum, item) => sum + Number(item.amount), 0);
  const varies = structure.items.some(
    (item) => (item.applies_to && item.applies_to !== "all") || item.transport_only,
  );
  const rows = [
    { label: "Day student", amount: billed("day", false) },
    { label: "Boarder", amount: billed("boarding", false) },
    { label: "Day student on school transport", amount: billed("day", true) },
    { label: "Boarder on school transport", amount: billed("boarding", true) },
  ];
  // Keep a transport row only when transport actually adds something.
  const cases = varies
    ? rows.filter((row, index) => index < 2 || row.amount > rows[index - 2].amount)
    : [{ label: "Every student", amount: Number(structure.total_amount) }];
  const amounts = cases.map((row) => row.amount);
  return { varies, cases, min: Math.min(...amounts), max: Math.max(...amounts) };
}

/** "UGX 450,000", or "UGX 450,000 – 1,250,000" when learners are billed differently. */
export function perStudentLabel(structure: FeeStructure) {
  const { min, max } = structureBilling(structure);
  return min === max ? `UGX ${min.toLocaleString()}` : `UGX ${min.toLocaleString()} – ${max.toLocaleString()}`;
}

export const audienceNote = (item: FeeStructureItem) =>
  [
    item.applies_to === "day" ? "Day students only" : item.applies_to === "boarding" ? "Boarders only" : "",
    item.transport_only ? "School transport only" : "",
  ]
    .filter(Boolean)
    .join(", ") || "Every student";

function matchesNonInvoicing(structure: FeeStructure, filters: FeeStructureFilters, context: FeeStructureContext) {
  const search = filters.search.trim().toLowerCase();
  if (search) {
    const haystack = [structure.name, structure.notes ?? "", structure.level, ...structure.items.map((item) => item.fee_name ?? "")]
      .join(" ")
      .toLowerCase();
    if (!haystack.includes(search)) return false;
  }
  if (filters.academicYear && String(structure.academic_year) !== filters.academicYear) return false;
  if (filters.term && context.termOf(structure.term)?.term !== filters.term) return false;
  if (filters.level && structure.level !== filters.level) return false;
  return true;
}

export function feeStructureInvoicingCounts(
  structures: FeeStructure[],
  filters: FeeStructureFilters,
  context: FeeStructureContext,
) {
  const matching = structures.filter((structure) => matchesNonInvoicing(structure, filters, context));
  const pending = matching.filter((structure) => structure.pending_invoice_count > 0).length;
  return { all: matching.length, pending, done: matching.length - pending };
}

export function filterFeeStructures(
  structures: FeeStructure[],
  filters: FeeStructureFilters,
  context: FeeStructureContext,
): FeeStructure[] {
  const rows = structures.filter(
    (structure) =>
      matchesNonInvoicing(structure, filters, context) &&
      (filters.invoicing === "all" ||
        (filters.invoicing === "pending") === structure.pending_invoice_count > 0),
  );
  const levelIndex = (level: string) => {
    const index = context.levels.indexOf(level);
    return index === -1 ? context.levels.length : index;
  };
  // Start date when the calendar has one, else the term id, which grows as
  // terms are added.
  const termKey = (structure: FeeStructure) =>
    context.termOf(structure.term)?.start_date ?? String(structure.term).padStart(10, "0");
  const byName = (a: FeeStructure, b: FeeStructure) => a.name.localeCompare(b.name);
  const byLevel = (a: FeeStructure, b: FeeStructure) => levelIndex(a.level) - levelIndex(b.level);
  const compare: Record<FeeStructureFilters["ordering"], (a: FeeStructure, b: FeeStructure) => number> = {
    "-term": (a, b) => termKey(b).localeCompare(termKey(a)) || byLevel(a, b) || byName(a, b),
    term: (a, b) => termKey(a).localeCompare(termKey(b)) || byLevel(a, b) || byName(a, b),
    level: (a, b) => byLevel(a, b) || termKey(b).localeCompare(termKey(a)) || byName(a, b),
    name: byName,
    "-total": (a, b) => structureBilling(b).max - structureBilling(a).max || byName(a, b),
  };
  return rows.sort(compare[filters.ordering]);
}

export function feeStructureExportColumns(termName: (structure: FeeStructure) => string): ExportColumn<FeeStructure>[] {
  return [
    { header: "Fee structure", accessor: (structure) => structure.name },
    { header: "Class level", accessor: (structure) => structure.level },
    { header: "Term", accessor: termName },
    {
      header: "Fee items",
      accessor: (structure) =>
        structure.items.map((item) => `${item.fee_name} (UGX ${Number(item.amount).toLocaleString()})`).join("; "),
    },
    {
      header: "Per student (UGX)",
      accessor: (structure) =>
        structureBilling(structure)
          .cases.map((row) => `${row.label}: ${row.amount.toLocaleString()}`)
          .join("; "),
    },
    { header: "Not yet invoiced", accessor: (structure) => structure.pending_invoice_count },
    { header: "Notes", accessor: (structure) => structure.notes || "" },
  ];
}
